import * as XLSX from 'xlsx';
import { BacktestResponse } from '../../services/StrategySystematicsService';

type Column = { name: string; values: Array<number | null> };

// Build a worksheet keyed on `date`, one column per series, aligned by row index.
const sheetFromColumns = (dates: string[], columns: Column[]): XLSX.WorkSheet => {
  const header = ['date', ...columns.map((c) => c.name)];
  const rows = dates.map((d, i) => {
    const row: Record<string, string | number | null> = { date: d };
    for (const c of columns) row[c.name] = c.values[i] ?? null;
    return row;
  });
  return XLSX.utils.json_to_sheet(rows, { header });
};

// One column per signal name from a column-per-name matrix.
const perSignalColumns = (names: string[], matrix: Array<Array<number | null>>): Column[] =>
  names.map((name, j) => ({ name, values: matrix[j] ?? [] }));

/**
 * Download every backtest time series as a multi-sheet Excel workbook. Full-sample sheets
 * (instrument + per-signal pipeline stages) sit on the whole-run date axis; the traded sheet
 * sits on the shorter OOS-stitched axis. Covers the full Q&A glossary of series.
 */
export const downloadSystematicsSeries = (result: BacktestResponse, strategyName: string, aum: number): void => {
  const { series } = result;
  const wb = XLSX.utils.book_new();

  // --- Full-sample instrument series (r_t, sigma_t, sigma_{t-1}, r~_t) ---
  XLSX.utils.book_append_sheet(
    wb,
    sheetFromColumns(series.dates, [
      { name: 'pnl (r_t)', values: series.pnl },
      { name: 'ann_vol (sigma_t)', values: series.ann_vol },
      { name: 'ann_vol_lag1 (sigma_t-1)', values: series.ann_vol_lag1 },
      { name: 'voladj_pnl (r~_t)', values: series.voladj_pnl },
    ]),
    'Instrument',
  );

  // --- Per-signal pipeline stages, one sheet each, column-per-signal ---
  const perSignalSheets: Array<[string, Array<Array<number | null>>]> = [
    ['Raw Signal (s_t)', series.raw_signal],
    ['Raw Signal Lag (s_t-1)', series.signal_lag],
    ['Signal PnL (p^s)', series.signal_pnl],
    ['Signal PnL Vol (v^s)', series.signal_pnl_vol],
    ['Signal Scaler (c^s)', series.signal_scaler],
    ['Signal Scaler Lag (c^s_t-1)', series.signal_scaler_lag],
    ['Normalized Signal (s^_t)', series.normalized_signal],
    ['Normalized Signal Lag (s^_t-1)', series.normalized_signal_lag],
    ['Normalized Signal PnL', series.normalized_signal_pnl],
  ];
  for (const [sheet, matrix] of perSignalSheets) {
    XLSX.utils.book_append_sheet(wb, sheetFromColumns(series.dates, perSignalColumns(series.names, matrix)), sheet);
  }

  // --- Traded OOS series (combined signal C_t, position omega_t, PnL) ---
  const position = result.pos.map((p) => (p === null || aum === 0 ? null : p / aum));
  XLSX.utils.book_append_sheet(
    wb,
    sheetFromColumns(result.dates, [
      { name: 'combined_signal (C_t)', values: result.combined_signal },
      { name: 'position (omega_t)', values: position },
      { name: 'position_notional', values: result.pos },
      { name: 'pnl', values: result.pnl },
      { name: 'cum_pnl', values: result.cum_pnl },
    ]),
    'Traded (OOS)',
  );

  const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  XLSX.writeFile(wb, `${strategyName}_series_${stamp}.xlsx`);
};
